import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppBar, FadeInAnimation, Dialog } from '../../../core/components/ui';
import toast from 'react-hot-toast';

const PHQ9_QUESTIONS = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed, or hopeless',
  'Trouble falling or staying asleep, or sleeping too much',
  'Feeling tired or having little energy',
  'Poor appetite or overeating',
  'Feeling bad about yourself — or that you are a failure',
  'Trouble concentrating on things, such as reading the newspaper or watching television',
  'Moving or speaking so slowly that other people could have noticed?',
  'Thoughts that you would be better off dead, or thoughts of hurting yourself',
];

const GAD7_QUESTIONS = [
  'Feeling nervous, anxious, or on edge',
  'Not being able to stop or control worrying',
  'Worrying too much about different things',
  'Trouble relaxing',
  'Being so restless that it is hard to sit still',
  'Becoming easily annoyed or irritable',
  'Feeling afraid, as if something awful might happen',
];

const OPTIONS = ['Not at all', 'Several days', 'More than half the days', 'Nearly every day'];

const AssessmentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const assessment = (location.state as any)?.assessment;

  const questions = assessment?.id === 'gad7' ? GAD7_QUESTIONS : PHQ9_QUESTIONS;

  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [resultOpen, setResultOpen] = useState(false);
  const [score, setScore] = useState(0);

  const allAnswered = Object.keys(answers).length === questions.length;

  const handleSubmit = () => {
    const total = Object.values(answers).reduce((sum, v) => sum + v, 0);
    setScore(total);
    setResultOpen(true);
  };

  const getSeverity = (s: number) => {
    if (s <= 4) return { label: 'Minimal', color: 'var(--color-success)' };
    if (s <= 9) return { label: 'Mild', color: '#F59E0B' };
    if (s <= 14) return { label: 'Moderate', color: '#F97316' };
    return { label: 'Severe', color: 'var(--color-error)' };
  };

  const severity = getSeverity(score);

  if (!assessment) {
    navigate(-1);
    return null;
  }

  return (
    <div className="page">
      <AppBar title={assessment.title} onBack={() => navigate(-1)} />

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 40px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--color-on-surface-variant)', lineHeight: 1.5 }}>
          Over the past 2 weeks, how often have you been bothered by any of the following problems?
        </p>

        {questions.map((q, i) => (
          <FadeInAnimation key={i} delay={i * 0.06}>
            <div className="card">
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, lineHeight: 1.4 }}>
                {i + 1}. {q}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {OPTIONS.map((opt, j) => (
                  <button
                    key={j}
                    onClick={() => setAnswers(a => ({ ...a, [i]: j }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', borderRadius: 10, width: '100%', textAlign: 'left',
                      background: answers[i] === j ? 'var(--color-primary-container)' : 'var(--color-surface-variant)',
                      border: answers[i] === j ? '1.5px solid var(--color-primary)' : '1.5px solid transparent',
                      color: answers[i] === j ? 'var(--color-on-primary-container)' : 'var(--color-on-surface-variant)',
                      fontWeight: answers[i] === j ? 600 : 400,
                      fontSize: 13, transition: 'all 0.18s',
                    }}
                  >
                    <div style={{
                      width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                      border: `2px solid ${answers[i] === j ? 'var(--color-primary)' : 'var(--color-outline)'}`,
                      background: answers[i] === j ? 'var(--color-primary)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {answers[i] === j && (
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'white' }} />
                      )}
                    </div>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </FadeInAnimation>
        ))}

        <div style={{ paddingTop: 8 }}>
          <button
            className="btn-gradient"
            onClick={handleSubmit}
            disabled={!allAnswered}
          >
            Submit Assessment
          </button>
          {!allAnswered && (
            <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--color-on-surface-variant)', marginTop: 8 }}>
              Please answer all {questions.length} questions to submit.
            </p>
          )}
        </div>
      </div>

      {/* Result Dialog */}
      <Dialog open={resultOpen} onClose={() => setResultOpen(false)}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>
            {score <= 9 ? '😊' : score <= 14 ? '😐' : '😟'}
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Assessment Complete</h3>
          <p style={{ fontSize: 13, color: 'var(--color-on-surface-variant)', marginBottom: 20 }}>
            {assessment.title}
          </p>
          <div style={{
            padding: '16px', borderRadius: 12,
            background: `${severity.color}18`,
            marginBottom: 20,
          }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: severity.color }}>{score}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: severity.color }}>{severity.label} Symptoms</div>
          </div>
          <p style={{ fontSize: 13, color: 'var(--color-on-surface-variant)', lineHeight: 1.5, marginBottom: 20 }}>
            Share these results with your therapist during your next session for professional guidance.
          </p>
          <button
            className="btn-primary"
            onClick={() => { setResultOpen(false); navigate(-1); }}
          >
            Done
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default AssessmentDetailsPage;
